import React, { useState, useMemo, useEffect } from 'react';
import { MATCH_WINNER_MARKET_ID } from '../constants/challenges.constants';
import type { ChallengeWithStats } from '../types/challengeCard.types';
import type { UserPredictions } from '../types/challengeCard.types';

interface UseChallengePredictionsProps {
  challenge: ChallengeWithStats;
  currentUserId: string;
}

interface UseChallengePredictionsReturn {
  userPredictions: UserPredictions;
  setUserPredictions: React.Dispatch<React.SetStateAction<UserPredictions>>;
  handlePickSelect: (betId: string, pick: 'home' | 'draw' | 'away' | null) => void;
  hasChanges: boolean;
}

function deriveUserPredictionsFromPicks(challenge: ChallengeWithStats, currentUserId: string): UserPredictions {
  const initial: UserPredictions = {};
  const picks1x2 = (challenge as { picks?: Array<{ matchId: string; userId: string; marketId: number; value?: string }> }).picks?.filter(
    (p) => p.userId === currentUserId && p.marketId === MATCH_WINNER_MARKET_ID
  ) ?? [];
  picks1x2.forEach((p) => {
    const v = String(p.value ?? '').toLowerCase();
    initial[p.matchId] = (v === 'home' || v === 'draw' || v === 'away' ? v : 'draw') as 'home' | 'draw' | 'away';
  });
  return initial;
}

export const useChallengePredictions = ({
  challenge,
  currentUserId,
}: UseChallengePredictionsProps): UseChallengePredictionsReturn => {
  const [userPredictions, setUserPredictions] = useState<UserPredictions>(() =>
    deriveUserPredictionsFromPicks(challenge, currentUserId)
  );

  const initialFromPicks = useMemo(
    () => deriveUserPredictionsFromPicks(challenge, currentUserId),
    [challenge, currentUserId]
  );

  // Sync local state when server picks actually change (e.g. after refetch), so UI shows saved state
  const picksFingerprint = useMemo(() => {
    const picks1x2 = (challenge as { picks?: Array<{ matchId: string; userId: string; marketId: number; value?: string }> }).picks?.filter(
      (p) => p.userId === currentUserId && p.marketId === MATCH_WINNER_MARKET_ID
    ) ?? [];
    return JSON.stringify(
      picks1x2.map((p) => ({ m: p.matchId, v: p.value ?? '' })).sort((a, b) => a.m.localeCompare(b.m))
    );
  }, [challenge?.picks, currentUserId]);

  useEffect(() => {
    setUserPredictions(deriveUserPredictionsFromPicks(challenge, currentUserId));
  }, [picksFingerprint, challenge, currentUserId]);

  const handlePickSelect = (
    betId: string,
    pick: 'home' | 'draw' | 'away' | null
  ) => {
    setUserPredictions((prev) => ({
      ...prev,
      [betId]: pick,
    }));
  };

  const hasChanges = useMemo(() => {
    const allMatchIds = new Set([...Object.keys(userPredictions), ...Object.keys(initialFromPicks)]);
    for (const matchId of allMatchIds) {
      if ((userPredictions[matchId] ?? null) !== (initialFromPicks[matchId] ?? null)) return true;
    }
    return false;
  }, [userPredictions, initialFromPicks]);

  return {
    userPredictions,
    setUserPredictions,
    handlePickSelect,
    hasChanges,
  };
};

